import React, {memo, useEffect} from 'react'
import {Button, Dropdown, Header, Icon, Label, Message, Popup, Radio, Table} from 'semantic-ui-react'
import {fromISOString, isDaysOld, toLocaleDateString} from '../../../../../../util'
import {
  withChildPathProps,
  withEnvironmentData,
  withErrorHandling,
  withHelmet,
  withPropsPresent,
  withResourceData,
  withServiceData,
  withVersionData
} from '../../../../../../hoc'
import {CodeEditor} from '../../../../../shared'
import {toggleResponse, cloneResponse, saveResponseScript} from '../../../../../../backend'
import {useSafeState} from '../../../../../../hook'

const _Responses = (props) => {
  let {
    service,
    version,
    environment,
    resource,
    navigate,
    mutateResource,
    saveResponseScript,
    toggleResponse,
    cloneResponse,
    basePath = '/c/services/:serviceId/versions/:versionId/environments/:environmentId/resources/:resourceId/responses',
    children
  } = props

  const [state, setState] = useSafeState({
    cloning: '',
    script: '',
    updatingScript: '',
    updatedScript: '',
    responses: []
  })

  useEffect(() => {
    setState({...state, responses: resource.responses})
  }, [resource])

  const getPathPrefix = () => {
    return basePath
      .replace(':serviceId', service.id)
      .replace(':versionId', version.id)
      .replace(':environmentId', environment.id)
      .replace(':resourceId', resource.id)
  }

  const onAdd = (e, response) => {
    e.preventDefault()
    navigate(`${getPathPrefix()}/add`)
  }

  const onEdit = (e, response) => {
    e.preventDefault()
    navigate(`${getPathPrefix()}/${response.id}/edit`)
  }

  const onRemove = (e, response) => {
    e.preventDefault()
    navigate(`${getPathPrefix()}/${response.id}/remove`)
  }

  const onCopyTo = (e, response) => {
    e.preventDefault()
    navigate(`${getPathPrefix()}/${response.id}/copy-to/${service.id}`)
  }

  let timeoutHandle

  const onUpdateScript = async (e, response) => {
    e.preventDefault()
    if (timeoutHandle) {
      clearTimeout(timeoutHandle)
    }
    setState({...state, updatingScript: response.id, updatedScript: ''})
    response = {...response, script: state.script}
    response = await saveResponseScript(response.id, response.script)
    timeoutHandle = setTimeout(() => setState({...state, updatedScript: ''}), 3000)

    setState({...state, updatingScript: '', updatedScript: response.id})

    mutateResource()
  }

  const onToggle = async (e, response) => {
    e.preventDefault()
    const originalResponses = [...state.responses]
    response = {...response, selected: !response.selected}
    let responses = [...state.responses]
    const atLeastTwoUnscriptedResponses = responses.filter(r => !r.script).length >= 2
    if (atLeastTwoUnscriptedResponses) {
      responses = responses.map(r => {
        return {...r, selected: false}
      })
    }
    responses = responses.map(r => r.id === response.id ? response : r)
    setState({...state, responses})
    try {
      await toggleResponse(response.id)
    } catch (e) {
      setState({...state, responses: originalResponses})
    }
  }

  const onClone = async (e, response) => {
    e.preventDefault()
    setState({...state, cloning: response.id, response})
    await cloneResponse(response.id)
    setState({...state, cloning: '', resource})
    mutateResource()
  }

  const getLastAccessedContent = (resp) => {
    const lastAccessedAt = resp.lastAccessedAt
    if (!lastAccessedAt) {
      return 'n/a'
    }
    const date = fromISOString(lastAccessedAt)
    const text = toLocaleDateString(date)
    const isScripted = !!resp.script
    const isThirtyOrMoreDaysOld = isDaysOld(date, 30)
    const isFifteenOrMoreDaysOld = isDaysOld(date, 15)
    const isSevenOrMoreDaysOld = isDaysOld(date, 7)
    if (isScripted && (isThirtyOrMoreDaysOld || isFifteenOrMoreDaysOld || isSevenOrMoreDaysOld)) {
      let color = ''
      let title = ''
      if (isThirtyOrMoreDaysOld) {
        color = 'red'
        title = 'Response not used in 30 days'
      } else if (isFifteenOrMoreDaysOld) {
        color = 'orange'
        title = 'Response not used in 15 days'
      } else if (isSevenOrMoreDaysOld) {
        color = 'yellow'
        title = 'Response not used in 7 days'
      }
      return (
        <>
          <Label title={title} circular color={color} empty key={color} style={{marginRight: '10px'}}/>
          <span>{text}</span>
        </>
      )
    } else {
      return text
    }
  }

  const getContentType = (resp) => {
    const {headers = {}} = resp
    return resp.contentType || headers['content-type'] || headers['Content-Type']
  }

  return (
    <div>
      <Header as='h4' dividing>Responses</Header>
      {
        state.responses.length > 0 && (
          <Table celled data-testid='responses'>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>Status</Table.HeaderCell>
                <Table.HeaderCell>Name</Table.HeaderCell>
                <Table.HeaderCell>Content-Type</Table.HeaderCell>
                <Table.HeaderCell>Weight</Table.HeaderCell>
                <Table.HeaderCell>Active</Table.HeaderCell>
                <Table.HeaderCell>Last Used</Table.HeaderCell>
                <Table.HeaderCell></Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {
                state.responses.map((resp) => (
                  <Table.Row key={resp.id} title={getContentType(resp)} data-test='response-table-row'>
                    <Table.Cell width='1' data-test='response-type'>
                      {
                        resp.type === 'static' ? resp.status : (
                          <Label color='grey' ribbon data-test='response-proxy-label'>
                            Proxied
                          </Label>
                        )
                      }
                    </Table.Cell>
                    {/*<Table.Cell*/}
                    {/*  verticalAlign='top'>{resp.type === 'proxy' ? 'n/a' : resp.contentType}*/}
                    {/*</Table.Cell>*/}
                    <Table.Cell verticalAlign='top' width='4' data-test='response-name'>
                      <a title='View/Edit' href=''
                         data-test='response-view-link'
                         style={{fontWeight: 'bold', textDecoration: 'underline'}}
                         onClick={(e) => onEdit(e, resp)}>{resp.name}</a>
                    </Table.Cell>
                    <Table.Cell verticalAlign='top' width='3'
                                data-test='response-content-type'>{resp.contentType}</Table.Cell>
                    <Table.Cell verticalAlign='top' width='1' data-test='response-weight'>{resp.weight}</Table.Cell>
                    <Table.Cell width='2'>
                      {
                        // !!resp.script === true && (<Icon name='code' title='activated via script'/>)
                        !!resp.script === true && (
                          <Popup
                            trigger={<Icon data-test='response-script-icon' name='code' title='activated via script'/>}
                            hoverable
                            position='left center'
                            onOpen={() => {
                              setState({...state, script: resp.script})
                            }}
                            onClose={() => setState({
                              ...state,
                              script: '',
                              updatingScript: '',
                              updatedScript: ''
                            })}>
                            <div data-test='response-script-popup-editor'>
                              <CodeEditor
                                cursorStart={-1}
                                readOnly={false}
                                mode='javascript'
                                theme='xcode'
                                height='65vh'
                                width='45vw'
                                showPrintMargin={false}
                                value={state.script}
                                editorProps={{$blockScrolling: true}}
                                onChange={(value) => setState({...state, script: value})}
                              />
                            </div>
                            {
                              state.updatedScript === resp.id && (
                                <Message success data-test='response-popup-script-success-message'>Successfully updated
                                  script!</Message>)
                            }
                            <div style={{marginTop: '2em'}}>
                              <Button loading={state.updatingScript === resp.id}
                                      data-test='response-popup-script-update-button'
                                      onClick={(e) => onUpdateScript(e, resp)} primary>
                                <Icon name='save'/>
                                Save Script
                              </Button>
                            </div>
                          </Popup>
                        )
                      }
                      {
                        !resp.script === true && (
                          <Radio toggle
                                 checked={resp.selected}
                                 data-test='response-select-radio'
                                 onChange={(e) => onToggle(e, resp)}/>
                        )

                      }
                    </Table.Cell>
                    <Table.Cell
                      verticalAlign='top'>{getLastAccessedContent(resp)}</Table.Cell>
                    <Table.Cell verticalAlign='top' textAlign='center' width='1'>
                      <Dropdown icon='setting' loading={state.cloning === resp.id} data-test='response-actions-menu'>
                        <Dropdown.Menu>
                          <Dropdown.Item as='a' onClick={(e) => onEdit(e, resp)} data-test='response-edit-action'>
                            <Icon name='edit'/> Edit
                          </Dropdown.Item>
                          <Dropdown.Item as='a' onClick={(e) => onRemove(e, resp)} data-test='response-remove-action'>
                            <Icon name='remove'/> Remove
                          </Dropdown.Item>
                          <Dropdown.Item as='a'
                                         data-test='response-clone-action'
                                         onClick={(e) => onClone(e, resp)}>
                            <Icon name='copy'/> Clone
                          </Dropdown.Item>
                          <Dropdown.Item as='a'
                                         data-test='response-copy-to-action'
                                         onClick={(e) => onCopyTo(e, resp)}>
                            <Icon name='arrow right'/> Copy To
                          </Dropdown.Item>
                        </Dropdown.Menu>
                      </Dropdown>
                    </Table.Cell>
                  </Table.Row>
                ))
              }
            </Table.Body>
          </Table>)
      }
      {
        state.responses.length === 0 && (
          <div data-testid='responses'>
            No responses defined
          </div>
        )
      }
      <Button size='tiny'
              basic
              icon='add'
              content='Add Response'
              style={{marginTop: '25px'}}
              data-testid='responses-add-button'
              onClick={(e) => onAdd(e)}/>
      {children}
    </div>
  )
}


let Responses = (props) => {
  const {handleError} = props
  props = {
    ...props,
    cloneResponse: handleError(cloneResponse),
    toggleResponse: handleError(toggleResponse),
    saveResponseScript: handleError(saveResponseScript)
  }
  return (<_Responses {...props}/>)
}

Responses = withPropsPresent(Responses, ['service', 'version', 'environment', 'resource'])
Responses = withHelmet(Responses, 'Responses')
Responses = withResourceData(Responses, 'Loading resource...')
Responses = withEnvironmentData(Responses, 'Loading environment...')
Responses = withVersionData(Responses, 'Loading version...')
Responses = withServiceData(Responses, 'Loading service...')
Responses = withChildPathProps(Responses)
Responses = withErrorHandling(Responses)
Responses = memo(Responses)

export {_Responses, Responses}

export default Responses